import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';

const wrapperStyles = {
  display: 'inline-block',
  whiteSpace: 'pre-wrap',
  position: 'relative',
};

const srOnlyStyles = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  border: 0,
};

const DEFAULT_CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-={}[]:<>,.?/';

const DecryptedText = ({
  text = '',
  speed = 50,
  maxIterations = 10,
  sequential = false,
  revealDirection = 'start',
  useOriginalCharsOnly = false,
  characters = DEFAULT_CHARSET,
  className = '',
  parentClassName = '',
  encryptedClassName = '',
  animateOn = 'hover',
  ...props
}) => {
  const [displayText, setDisplayText] = useState(text);
  const [isHovering, setIsHovering] = useState(false);
  const [isScrambling, setIsScrambling] = useState(false);
  const [revealedIndices, setRevealedIndices] = useState(() => new Set());
  const [hasAnimated, setHasAnimated] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    setDisplayText(text);
    setRevealedIndices(new Set());
    setHasAnimated(false);
    if (animateOn === 'view') {
      setIsHovering(false);
    }
  }, [text, animateOn]);

  useEffect(() => {
    let intervalId;
    let currentIteration = 0;

    const getNextIndex = (revealedSet) => {
      const total = text.length;
      if (total === 0) {
        return 0;
      }

      if (revealDirection === 'end') {
        return total - 1 - revealedSet.size;
      }

      if (revealDirection === 'center') {
        const middle = Math.floor(total / 2);
        const offset = Math.floor(revealedSet.size / 2);
        const candidate = revealedSet.size % 2 === 0 ? middle + offset : middle - offset - 1;

        if (candidate >= 0 && candidate < total && !revealedSet.has(candidate)) {
          return candidate;
        }

        for (let i = 0; i < total; i += 1) {
          if (!revealedSet.has(i)) {
            return i;
          }
        }

        return 0;
      }

      return revealedSet.size;
    };

    const sourceCharacters = useOriginalCharsOnly
      ? Array.from(new Set(text.split(''))).filter((char) => char.trim().length)
      : characters.split('');

    const shuffleText = (originalText, currentRevealed) => {
      if (useOriginalCharsOnly) {
        const blueprint = originalText.split('').map((char, index) => ({
          char,
          index,
          reveal: currentRevealed.has(index),
          isSpace: char === ' ',
        }));

        const pool = blueprint.filter((item) => !item.isSpace && !item.reveal).map((item) => item.char);

        for (let i = pool.length - 1; i > 0; i -= 1) {
          const j = Math.floor(Math.random() * (i + 1));
          [pool[i], pool[j]] = [pool[j], pool[i]];
        }

        let cursor = 0;
        return blueprint
          .map((item) => {
            if (item.isSpace) {
              return ' ';
            }
            if (item.reveal) {
              return originalText[item.index];
            }
            return pool[cursor++] || ' ';
          })
          .join('');
      }

      return originalText
        .split('')
        .map((char, index) => {
          if (char === ' ') {
            return ' ';
          }
          if (currentRevealed.has(index)) {
            return originalText[index];
          }
          const randomIndex = Math.floor(Math.random() * sourceCharacters.length);
          return sourceCharacters[randomIndex] || char;
        })
        .join('');
    };

    if (isHovering && text.length > 0) {
      setIsScrambling(true);
      intervalId = setInterval(() => {
        setRevealedIndices((prev) => {
          if (sequential) {
            if (prev.size < text.length) {
              const nextIndex = getNextIndex(prev);
              const updated = new Set(prev);
              updated.add(nextIndex);
              setDisplayText(shuffleText(text, updated));
              return updated;
            }
            clearInterval(intervalId);
            setIsScrambling(false);
            setDisplayText(text);
            return prev;
          }

          setDisplayText(shuffleText(text, prev));
          currentIteration += 1;
          if (currentIteration >= maxIterations) {
            clearInterval(intervalId);
            setIsScrambling(false);
            setDisplayText(text);
          }
          return prev;
        });
      }, speed);
    } else {
      setDisplayText(text);
      setRevealedIndices(new Set());
      setIsScrambling(false);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isHovering, text, speed, maxIterations, sequential, revealDirection, characters, useOriginalCharsOnly]);

  useEffect(() => {
    if (animateOn !== 'view' && animateOn !== 'both') {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setIsHovering(true);
            setHasAnimated(true);
          }
        });
      },
      {
        root: null,
        threshold: 0.2,
      },
    );

    const element = containerRef.current;
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
      observer.disconnect();
    };
  }, [animateOn, hasAnimated]);

  const hoverHandlers =
    animateOn === 'hover' || animateOn === 'both'
      ? {
          onMouseEnter: () => setIsHovering(true),
          onMouseLeave: () => {
            setIsHovering(false);
            setHasAnimated(false);
          },
          onFocus: () => setIsHovering(true),
          onBlur: () => setIsHovering(false),
        }
      : {};

  return (
    <motion.span
      ref={containerRef}
      className={parentClassName}
      style={wrapperStyles}
      {...hoverHandlers}
      {...props}
    >
      <span style={srOnlyStyles}>{text}</span>
      <span aria-hidden="true">
        {displayText.split('').map((char, index) => {
          const isRevealed = revealedIndices.has(index);
          const shouldShowFinalChar = !isScrambling || !isHovering;
          const appliedClass = isRevealed || shouldShowFinalChar ? className : encryptedClassName;
          return (
            <span key={`${char}-${index}`} className={appliedClass}>
              {char}
            </span>
          );
        })}
      </span>
    </motion.span>
  );
};

export default DecryptedText;
